import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useState } from "react";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { dayjs } from "@boo/shared";
import { useApiKeysState } from "../store/useSettingsStore";
import { INPUT_CLASS } from "../types/settings.types";
import NewKeyBanner from "./NewKeyBanner";

export default function ApiKeysTab() {
  const {
    keys,
    loadingKeys,
    newRawKey,
    apiKeyError,
    deletingKeyId,
    createKey,
    deleteKey,
    dismissNewKey,
  } = useApiKeysState();

  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);

  async function handleCreate(e: { preventDefault(): void }) {
    e.preventDefault();
    const name = newKeyName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await createKey(name);
      setNewKeyName("");
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      {newRawKey && (
        <NewKeyBanner rawKey={newRawKey} onDismiss={dismissNewKey} />
      )}
      {apiKeyError && (
        <p className="text-sm text-red-500 mb-4">{apiKeyError}</p>
      )}

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            Create new key
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="text"
              placeholder="Key name (e.g. slip-uploader)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              className={`flex-1 min-w-0 ${INPUT_CLASS} py-2`}
            />
            <Button
              type="submit"
              disabled={!newKeyName.trim()}
              loading={creating}
              size="sm"
              className="shrink-0"
            >
              {!creating && <Plus className="w-4 h-4 mr-1" />}
              {creating ? "Creating…" : "Create"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Active keys
            {!loadingKeys && (
              <span className="ml-2 font-normal text-muted-foreground">
                ({keys.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingKeys ? (
            <ListRowsSkeleton rows={2} />
          ) : keys.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No API keys yet
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {keys.map((k) => (
                <li
                  key={k.id}
                  className="flex items-center justify-between py-3 gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {k.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Created {dayjs(k.createdAt).format("D MMM YYYY")}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteKey(k.id)}
                    disabled={deletingKeyId === k.id}
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-40"
                    aria-label={`Delete ${k.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}
